/**
 * ⟦C21 · A-146 §2⟧ **L'ordre de parole de Matt** — la fonction pure qui répond, à tout instant
 * de la chambre, à « qu'est-ce que Matt dit maintenant ? ».
 *
 * Remplace les DEUX surfaces d'avant ce chantier (`ModeDecouverte`, le voile bloquant plein
 * écran ; `InfoBulleDecouverte`, la bulle ancrée) par UNE seule `BoiteDialogue` (C20), posée au-
 * dessus de la fenêtre « Actions » sur ordinateur, au-dessus des onglets sur mobile — elle ne
 * bouge plus jamais, donc ce module n'a plus besoin de calculer où la poser. Ce qui bougeait
 * (le halo pulsé sur l'élément dont Matt parle) reste : `haloDeReplique` en est la seule
 * responsable désormais.
 *
 * **Trois priorités, dans cet ordre, jamais deux choses à la fois :**
 * 1. la prochaine leçon **bloquante** due (briefing/évènement/horloge/commentaires) — les
 *    actions flottantes se grisent tant qu'elle est là (`Studio.tsx`) ;
 * 2. la prochaine **info-bulle non bloquante** due — le jeu continue derrière ;
 * 3. le rappel **« petit écran »**, une fois par partie, sur un appareil étroit — la dernière
 *    roue de secours, jamais devant une vraie leçon de jeu qui, elle, a quelque chose à
 *    apprendre.
 *
 * Aucune mécanique nouvelle : les deux premières priorités **délèguent** à
 * `game/decouverte.ts` (`prochaineLecon`/`prochaineInfoBulle`), qui reste la seule source de
 * vérité sur QUAND une leçon est due — ce module ne fait qu'les ORDONNER entre elles et avec le
 * petit écran, et dire où regarder.
 */

import { lecon, prochaineInfoBulle, prochaineLecon, repliquesDe, type Lecon, type LeconId } from "./decouverte";
import type { GameState } from "@maxime/shared";

/**
 * Le pseudo-identifiant du rappel petit écran. Volontairement ABSENT de `LECONS`
 * (`game/decouverte.ts`) : son déclencheur est le viewport, pas l'état de la run — une notion
 * que la table des leçons n'a pas à connaître. Il partage néanmoins la MÊME mémoire "vues" que
 * les leçons (`decouverteStore`), pour rester "une fois par partie" avec le même mécanisme que
 * le reste — jamais une deuxième case à cocher ailleurs.
 */
export const ID_PETIT_ECRAN: LeconId = "petit_ecran";

/**
 * ⟦C21 §2.4⟧ Verbatim du chantier — conservé tel quel : c'est la source, et `REPLIQUE_PETIT_ECRAN`
 * (ci-dessous) en est la version courte.
 */
export const REPLIQUE_PETIT_ECRAN_LONGUE =
  "Petit écran : les mini-jeux se jouent au clavier, et les jauges passent en colonne. Tu peux continuer ici, mais tu joues avec une main dans le dos.";

/**
 * ⟦C57 · lot 4 · A-174⟧ **Ce que Matt DIT** : une idée, une phrase (« une réplique de Matt = une idée, une
 * ligne »). Exporté pour que `useMattChambre` n'ait pas à le retaper ; le texte d'avant reste dans
 * `REPLIQUE_PETIT_ECRAN_LONGUE`, jamais réécrit.
 */
export const REPLIQUE_PETIT_ECRAN = "Petit écran : mini-jeux au clavier, jauges en colonne.";

/**
 * ⟦C27 · A-153⟧ Le pseudo-identifiant de la réplique qui suit le glissement de l'interface, à la
 * création d'une chaîne (`screens/Accueil/Accueil.tsx` a déjà joué les 4 répliques de l'accueil
 * avec la voix ; `stores/accueilStore.ts` marque `briefing` vue au moment où l'accueil cède la
 * place au glissement, pour que cette leçon-là ne se rejoue jamais juste après). Absent de
 * `LECONS` (`game/decouverte.ts`) pour la même raison que `ID_PETIT_ECRAN` : son déclencheur
 * n'est pas un fait de l'état de la run mais un moment de l'écran (la fin du glissement), et
 * `useMattChambre` le pilote directement plutôt que de passer par `paroleDue`/`prochaineInfoBulle`
 * (ne JAMAIS le rendre `quand: () => true` dans `LECONS`, ça referait apparaître cette réplique
 * pour toute run qui ne passe jamais par l'accueil : une reprise, une vieille sauvegarde).
 */
export const ID_APRES_ACCUEIL: LeconId = "apres_accueil";

/**
 * ⟦C27 · A-153⟧ Verbatim de l'accueil version 2 (`docs/REPLIQUES-MATT.md`), à valider par Matt,
 * conservé tel quel : c'est la source, et `REPLIQUE_APRES_ACCUEIL` (ci-dessous) en est la version courte.
 */
export const REPLIQUE_APRES_ACCUEIL_LONGUE =
  "Un seul geste te fait avancer : publier. Le reste, je te montre en route.";

/**
 * ⟦C27 · A-153 · C57 lot 4⟧ Ce que Matt DIT juste après l'accueil : UNE phrase de 12 mots au plus, reprise
 * ici plutôt que retapée dans `useMattChambre`. Non bloquante, sans voix (§3 du chantier), un seul halo sur
 * « Publier un short ».
 */
export const REPLIQUE_APRES_ACCUEIL = "Un seul geste te fait avancer : publier.";

export interface ParoleMatt {
  id: LeconId;
  /** `true` : les actions flottantes se grisent tant que cette parole est à l'écran. */
  bloquante: boolean;
  repliques: readonly string[];
}

/**
 * Qu'est-ce que Matt dit maintenant ? `null` : rien — la chambre est calme, l'état 2 de la
 * maquette F3. `vues` : la mémoire de `decouverteStore` (leçons ET petit écran, une seule
 * liste, l'esprit "une seule mémoire" déjà posé par ⟦A-129 · C11⟧). `tourDeLaDerniere` : le
 * tour de la dernière leçon BLOQUANTE refermée (règle "une par tour", inchangée). `etroit` :
 * l'appareil est sous le seuil de `BandeauPetitEcran` (960 px) — un fait du viewport, jamais lu
 * ici depuis `matchMedia` : c'est à l'appelant de le mesurer, pour que cette fonction reste pure
 * et testable sans DOM.
 */
export function paroleDue(
  state: GameState,
  vues: ReadonlySet<LeconId>,
  tourDeLaDerniere: number | null,
  etroit: boolean,
): ParoleMatt | null {
  // Même garde que `prochaineLecon`/`prochaineInfoBulle` : une run finie n'apprend plus rien,
  // et ça vaut aussi pour le petit écran — sans ce garde, lui seul continuerait de parler
  // par-dessus un écran de fin.
  if (state.status !== "active") return null;

  const bloquante = prochaineLecon(state, vues, tourDeLaDerniere);
  if (bloquante) {
    return { id: bloquante.id, bloquante: true, repliques: repliquesDe(bloquante) };
  }

  const bulle = prochaineInfoBulle(state, vues);
  if (bulle) {
    return { id: bulle.id, bloquante: false, repliques: repliquesDe(bulle) };
  }

  if (etroit && !vues.has(ID_PETIT_ECRAN)) {
    return { id: ID_PETIT_ECRAN, bloquante: false, repliques: [REPLIQUE_PETIT_ECRAN] };
  }

  return null;
}

/**
 * ⟦C21 §2.1⟧ Le halo pulsé pour LA RÉPLIQUE COURANTE d'une leçon bloquante donnée — pas pour la
 * leçon entière. Les trois autres bloquantes suivent la même idée, découpage libre (§2.1 :
 * « halo sur l'élément concerné quand il existe »).
 *
 * ⟦C22 · A-151⟧ Le briefing rejoue désormais l'accueil version 1 (`REPLIQUES_BRIEFING`,
 * `game/decouverte.ts`, la 1re réplique a déjà été dite par l'intro qui précède) : rien sur la
 * 1re et la 3e (elles posent le décor, aucun élément précis à montrer), le panneau « Ma chaîne »
 * sur la 2e (« zéro abonné, une webcam »), le bouton Publier sur la 4e, à retenir (« vas-y »).
 * Avant ⟦C22⟧ c'était l'inverse (1re et 2e), verbatim de l'ancien texte du briefing, retiré
 * avec lui.
 *
 * Les valeurs sont des ids posés en `className` par `Studio.tsx` (`.halo-pulse`), jamais un
 * `data-decouverte-ancre` repositionné : la boîte ne bouge plus, direction F3, donc plus besoin
 * de mesurer un `getBoundingClientRect`.
 */
const HALO_BRIEFING: readonly (string | null)[] = [null, "panneau-chaine", null, "publier"];
const HALO_HORLOGE: readonly (string | null)[] = ["chrono", null, "dormir", null];
const HALO_COMMENTAIRES: readonly (string | null)[] = [null, "sante-mentale", "dormir"];

/**
 * Le halo pulsé pour la réplique `index` de la leçon `id` (bloquante ou non). Pour les 12
 * info-bulles non bloquantes (une seule réplique chacune), c'est simplement l'ancre déjà posée
 * sur la leçon (`Lecon.ancre`, lue par `InfoBulleDecouverte` avant ce chantier) — jamais une
 * deuxième table à tenir à jour en double. `null` : aucun halo pour cette réplique, jamais une
 * erreur (le petit écran, `evenement`, ou un index hors table).
 */
export function haloDeReplique(id: LeconId, index: number): string | null {
  if (id === "briefing") return HALO_BRIEFING[index] ?? null;
  if (id === "horloge") return HALO_HORLOGE[index] ?? null;
  if (id === "commentaires") return HALO_COMMENTAIRES[index] ?? null;
  // ⟦C27 · A-153⟧ Halo fixe sur « Publier un short » (même bouton que la 4e réplique de
  // l'ancien briefing, HALO_BRIEFING) : le seul geste que cette réplique demande de retenir.
  if (id === ID_APRES_ACCUEIL) return "publier";
  if (id === ID_PETIT_ECRAN || id === "evenement") return null;
  const l: Lecon | null = lecon(id);
  return l?.ancre ?? null;
}

/**
 * ⟦C29 · intégration · correctif tour 1⟧ Les classes CSS de la boîte de Matt sur mobile
 * (`Studio.tsx`), extraites en fonction PURE pour verrouiller par un test unitaire la règle qui
 * évite le défaut du 14/09 (« jusqu'à 6 taps réels nécessaires sur Dormir ») : une réplique NON
 * bloquante (`bloquante: false`, le jeu continue derrière) reçoit TOUJOURS `matt-mobile-bulle`,
 * quelle que soit la leçon — jamais une liste d'ids à tenir à jour en double. Une réplique
 * BLOQUANTE ne la reçoit JAMAIS (`matt.passer`/« Passer » doivent rester atteignables au tap,
 * rien d'actionnable ne vit dessous). Le fondu d'entrée (`matt-mobile-fondu`, ⟦C27 quater⟧) suit
 * `reducedMotion`, indépendamment du reste.
 *
 * ⟦C29 · intégration · correctif tour 3⟧ **`matt-mobile-bulle` ne veut plus dire « transparente
 * au pointeur ».** Le tour 1 faisait traverser le tap par `pointer-events: none` (`app.css`) —
 * corrigeait le défaut des 6 taps, mais rendait la boîte elle-même infermable au tap (elle n'a
 * plus aucun `pointerdown` à recevoir) : la vérification du tour 2 a trouvé la conséquence, la
 * réplique `apres_accueil` ne se refermait jamais sur mobile et réapparaissait après avoir été
 * masquée par une leçon bloquante (elle n'était jamais marquée vue). Corrigé à la racine plutôt
 * qu'en rustinant `pointer-events` : `app.css` **repositionne** désormais cette boîte AU-DESSUS
 * de la fenêtre Actions (`.pile-mobile-basse`, empilement flex, jamais un chevauchement) au lieu
 * de la poser par-dessus en la rendant transparente — un tap sur Dormir/Publier atteint donc
 * directement le bouton (rien de la boîte n'est plus jamais dessous), ET un tap sur la boîte
 * elle-même la ferme normalement, comme sur ordinateur (`useMattChambre.ts`,
 * `paroleNonBloquanteAffichee` ci-dessous, étend en plus la fermeture "au premier tap ailleurs"
 * à `apres_accueil`/`petit_ecran`, pas seulement à la bulle de `decouverteStore`). Cette fonction
 * ne change pas : `matt-mobile-bulle` reste le marqueur qui distingue la variante non bloquante,
 * seul son sens côté CSS change.
 */
export function classesBulleMattMobile(bloquante: boolean, reducedMotion: boolean): string {
  if (bloquante) return "matt-mobile";
  return `matt-mobile matt-mobile-bulle${reducedMotion ? "" : " matt-mobile-fondu"}`;
}

/**
 * ⟦C29 · intégration · correctif tour 3⟧ **Quelle parole NON bloquante est actuellement à
 * l'écran ?** Fonction PURE extraite de `useMattChambre` pour verrouiller par un test la portée
 * du correctif du point 6 : la fermeture "au premier tap/clic ailleurs" (`useMattChambre.ts`,
 * un `pointerdown` capturé au niveau du document) ne couvrait AVANT ce tour que `couranteBulle`
 * (la mémoire de `decouverteStore`) — ni `apres_accueil` ⟦C27 · A-153⟧ ni `petit_ecran` ⟦C21
 * §2.4⟧, qui vivent dans un état local à `useMattChambre` et ne se fermaient donc QUE par un
 * clic direct sur la boîte (`onSuite`/`avancer`), impossible sur mobile tant que `pointer-events:
 * none` la rendait infermable (voir `classesBulleMattMobile` ci-dessus). Résultat mesuré :
 * `apres_accueil` ne se marquait jamais vue sur mobile, et réapparaissait après chaque leçon
 * bloquante qui la masquait temporairement — exactement le défaut que le point 6 du chantier
 * demande de corriger « à la racine ». Même ordre de priorité que `activeId`
 * (`useMattChambre.ts`) une fois `courante` (bloquante) écartée : la bulle de `decouverteStore`
 * d'abord (elle est plus précisément ciblée, une leçon à la fois), puis `apres_accueil`, puis
 * `petit_ecran` — en pratique les trois sont déjà mutuellement exclusifs (`libre` dans
 * `useMattChambre.ts` ne pose jamais l'un par-dessus un autre), cet ordre ne fait que documenter
 * lequel gagnerait si cette garantie se relâchait un jour. `null` : rien de non bloquant à
 * l'écran — l'appelant ne pose alors aucun écouteur.
 */
export function paroleNonBloquanteAffichee(
  idBulle: LeconId | null,
  apresAccueilActif: boolean,
  petitEcranActif: boolean,
): LeconId | null {
  if (idBulle) return idBulle;
  if (apresAccueilActif) return ID_APRES_ACCUEIL;
  if (petitEcranActif) return ID_PETIT_ECRAN;
  return null;
}
